import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Fab, Tooltip } from '@mui/material'
import StopIcon from '@mui/icons-material/Stop'
import RestoreIcon from '@mui/icons-material/Restore'
import ControlBarItem from './ControlBarItem'
import { useStartRecordingMutation, useStopRecordingMutation, useRecoverRecordingsMutation } from '@/core/api/recording'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import { useRosterState } from 'amazon-chime-sdk-component-library-react'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'
import { getApiErrorMessage } from '@/utils/errors'
import { useIsDoctor } from '@/hooks/useStaffRole'
import { useUpdateConsultationMutation } from '@/core/services/consultation'
import useAutoRecording from '@/hooks/useAutoRecording'

interface RecordingControlProps {
  consultationId: string
  meetingId: string
}

export default function RecordingControl({ consultationId, meetingId }: RecordingControlProps) {
  const authUser = useAppSelector(selectAuthUser)
  const [startRecordingMutation, { isLoading: isStarting }] = useStartRecordingMutation()
  const [stopRecordingMutation, { isLoading: isCreatingTranscription, isSuccess }] = useStopRecordingMutation()
  const [recoverRecordings, { isLoading: isRecovering }] = useRecoverRecordingsMutation()
  const [updateConsultation] = useUpdateConsultationMutation()
  const isDoctor = useIsDoctor()
  const { roster } = useRosterState()
  const { enqueueSnackbar } = useSnackbar()
  const [canRecover, setCanRecover] = useState<boolean>(false)

  const isRecording = useMemo(() => {
    return _.some(roster, (attendee) => attendee.name === 'MediaPipeline')
  }, [roster])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRecording || isCreatingTranscription) {
        e.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isRecording, isCreatingTranscription])

  const handleStartRecording = useCallback(async () => {
    try {
      await startRecordingMutation({
        meetingId,
        consultationId,
      }).unwrap()
      enqueueSnackbar(t`Recording started successfully`, { variant: 'success', autoHideDuration: 3000 })
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error.message, t`An error occurred while starting the recording`), { variant: 'error', autoHideDuration: 3000 })
    }
  }, [startRecordingMutation, meetingId, consultationId, enqueueSnackbar])

  const handleStopRecording = useCallback(async () => {
    try {
      await stopRecordingMutation({ consultationId, userId: authUser?._id! }).unwrap()
      if (isSuccess) {
        enqueueSnackbar(t`Transcription created successfully`, { variant: 'success', autoHideDuration: 3000 })
      }
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error.message, t`An error occurred while stopping the recording`), { variant: 'error', autoHideDuration: 3000 })
      setCanRecover(true)
    }
  }, [stopRecordingMutation, consultationId, isSuccess, enqueueSnackbar])

  useAutoRecording({ consultationId, isRecording, isDoctor, handleStartRecording, updateConsultation })

  const toggleRecording = () => {
    if (!isDoctor) return
    if (isRecording) {
      handleStopRecording()
    } else if (!isRecording && isDoctor) {
      handleStartRecording()
    }
  }

  return (
    <>
      <ControlBarItem>
        <Tooltip title={isRecording ? 'Stop recording' : 'Start recording'} placement="bottom" arrow>
          <Fab
            color="primary"
            size="small"
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.2)',
              },
            }}
            onClick={toggleRecording}
            disabled={isCreatingTranscription || isStarting}
          >
            {isRecording ? <StopIcon fontSize="small" color="error" /> : <GraphicEqIcon fontSize="small" color="secondary" />}
          </Fab>
        </Tooltip>
      </ControlBarItem>

      {canRecover ? (
        <ControlBarItem>
          <Tooltip title={t`Recover recording`} placement="bottom" arrow>
            <Fab
              color="primary"
              size="small"
              sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
              disabled={isRecovering}
              onClick={async () => {
                try {
                  const resp = await recoverRecordings({ consultationId }).unwrap()
                  if (resp.ok) {
                    enqueueSnackbar(t`Recovery triggered`, { variant: 'success', autoHideDuration: 3000 })
                    setCanRecover(false)
                  } else {
                    enqueueSnackbar(resp.error || t`No recordings found to recover`, {
                      variant: 'warning',
                      autoHideDuration: 3000,
                    })
                  }
                } catch {
                  enqueueSnackbar(t`Recovery failed`, { variant: 'error', autoHideDuration: 3000 })
                  setCanRecover(true)
                }
              }}
            >
              <RestoreIcon fontSize="small" />
            </Fab>
          </Tooltip>
        </ControlBarItem>
      ) : null}
    </>
  )
}
